import { useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '@/stores/auth'
import { teachersApi } from '@/api/endpoints/teachers'
import { parishesApi } from '@/api/endpoints/parishes'
import { classesApi } from '@/api/endpoints/classes'
import { openEmailClient, getTeacherLoginEmailTemplate } from '@/utils/email'
import { useUI } from '@/stores/ui'
import Spinner from '@/components/common/Spinner'
import CreateTeacherModal from './modals/CreateTeacherModal'
import EditTeacherModal from './modals/EditTeacherModal'

interface TeacherUser {
  id: string
  first_name?: string
  last_name?: string
  email: string
  password?: string // Password is not returned from API, but we track it locally if available
  class_assignments?: string[] // Class names assigned to this teacher
}

export default function TeacherList({ open = false, onToggle }: { open?: boolean; onToggle?: () => void }) {
  const { user } = useAuth()
  const addToast = useUI((state) => state.addToast)
  const [teachers, setTeachers] = useState<TeacherUser[]>([])
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [editing, setEditing] = useState<TeacherUser | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState('')
  const [parishName, setParishName] = useState('')

  // Load teachers, parish name, and class assignments
  useEffect(() => {
    let mounted = true
    const run = async () => {
      if (!user?.parish_id) return
      setLoading(true)
      try {
        const [teachersData, parishesData, classesData] = await Promise.all([
          teachersApi.list(),
          parishesApi.list(),
          classesApi.list(),
        ])
        if (mounted) {
          const teachersList = Array.isArray(teachersData) ? (teachersData as TeacherUser[]) : []
          
          // Add class assignments to each teacher
          const classes = Array.isArray(classesData) ? classesData : []
          const teachersWithClasses = teachersList.map(teacher => {
            const assignedClasses = classes
              .filter(cls => cls.teacher_names?.some(tName => {
                // Match teacher by email or name
                const teacherName = `${teacher.first_name || ''} ${teacher.last_name || ''}`.trim()
                return tName.includes(teacherName) || tName.includes(teacher.email)
              }))
              .map(cls => cls.name)
            
            return {
              ...teacher,
              class_assignments: assignedClasses,
            }
          })
          
          setTeachers(teachersWithClasses)
          
          const activeParish = Array.isArray(parishesData) ? parishesData.find(p => p.id === user.parish_id) : null
          if (activeParish) setParishName(activeParish.name)
        }
      } catch {
        if (mounted) setTeachers([])
      } finally {
        if (mounted) setLoading(false)
      }
    }
    run()
    return () => { mounted = false }
  }, [user?.parish_id])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return teachers
    return teachers.filter(t =>
      `${t.first_name || ''} ${t.last_name || ''} ${t.email} ${(t.class_assignments || []).join(' ')}`.toLowerCase().includes(q)
    )
  }, [teachers, query])

  const handleSendLoginEmail = (teacher: TeacherUser) => {
    const loginUrl = window.location.origin || 'https://www.catechismcloud.com'
    const currentYear = new Date().getFullYear()
    
    const template = getTeacherLoginEmailTemplate({
      teacherFirstName: teacher.first_name || 'Teacher',
      teacherEmail: teacher.email,
      teacherPassword: teacher.password, // May be undefined if not available
      parishName: parishName || 'Parish',
      adminTitle: user?.title || undefined,
      adminLastName: user?.last_name || 'Administrator',
      loginUrl,
      currentYear,
    })

    openEmailClient(teacher.email, template)
    addToast({
      message: `Email client opened with login credentials for ${teacher.first_name || teacher.email}`,
      type: 'success',
    })
  }

  const handleDelete = async (teacherId: string, teacherName: string) => {
    if (deleteConfirm !== 'delete') {
      addToast({ message: 'Please type "delete" to confirm', type: 'error' })
      return
    }

    setDeleting(teacherId)
    try {
      await teachersApi.remove(teacherId)
      addToast({ message: `Teacher ${teacherName} deleted successfully`, type: 'success' })
      setTeachers(prev => prev.filter(t => t.id !== teacherId))
      setDeleteConfirm('')
      setDeleting(null)
    } catch (error: any) {
      console.error('Failed to delete teacher:', error)
      addToast({
        message: error.response?.data?.detail || 'Failed to delete teacher',
        type: 'error',
      })
      setDeleting(null)
    }
  }

  return (
    <motion.div
      className="mt-6 p-5 rounded-xl border border-gray-200 bg-white shadow-sm"
      aria-busy={loading}
      initial={{ y: 8, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
    >
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={onToggle} aria-expanded={open} className="flex items-center gap-2 text-lg font-semibold text-gray-900 hover:text-gray-700 focus-ring rounded px-1 -ml-1">
          <span className={`text-gray-400 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}>▸</span>
          Teachers
        </button>
        <div className="flex items-center gap-2">
          <input
            className="input-field w-64 focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow"
            placeholder="Search teachers…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search teachers"
          />
          <button className="btn-primary text-sm transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98]" onClick={() => setShowCreate(true)} disabled={loading} aria-disabled={loading}>
            Add Teacher
          </button>
        </div>
      </div>
      {open && (<>
      {loading && <Spinner label="Loading teachers…" />}
      {!loading && (
        <div className="divide-y divide-gray-100 rounded-lg border border-gray-100">
          {filtered.length === 0 ? (
            <div className="p-4 text-sm text-gray-500">No teachers found.</div>
          ) : (
            filtered.map(t => (
              <div key={t.id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="font-medium mb-1">{t.first_name} {t.last_name}</div>
                    <div className="text-xs text-gray-600 mb-1">{t.email}</div>
                    {t.class_assignments && t.class_assignments.length > 0 && (
                      <div className="text-xs text-gray-500 mt-1">
                        Classes: {t.class_assignments.join(', ')}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className="btn-secondary text-xs focus-ring"
                      onClick={() => handleSendLoginEmail(t)}
                      title={`Open email client with pre-filled teacher login credentials for ${t.first_name || t.email}. Note: Teachers cannot reset their own passwords.`}
                      aria-label={`Send login email to ${t.first_name || t.email}`}
                    >
                      📧 Send Login
                    </button>
                    <button
                      className="btn-secondary text-xs focus-ring"
                      onClick={() => setEditing(t)}
                      disabled={deleting === t.id}
                      aria-label={`Edit ${t.first_name || t.email}`}
                    >
                      Edit
                    </button>
                    {deleting === t.id ? (
                      <div className="flex items-center gap-2 border-2 border-red-300 bg-red-50 rounded px-3 py-1">
                        <input
                          className="text-xs border-red-300 rounded focus:outline-none focus:ring-2 focus:ring-red-400/60"
                          placeholder="Type 'delete'"
                          value={deleteConfirm}
                          onChange={(e) => setDeleteConfirm(e.target.value)}
                          autoFocus
                          disabled={deleting !== t.id}
                        />
                        <button
                          className="btn-secondary text-xs bg-red-600 text-white hover:bg-red-700 focus-ring"
                          onClick={() => handleDelete(t.id, `${t.first_name} ${t.last_name}`)}
                          disabled={deleting !== t.id}
                        >
                          Delete
                        </button>
                        <button
                          className="btn-secondary text-xs focus-ring"
                          onClick={() => {
                            setDeleting(null)
                            setDeleteConfirm('')
                          }}
                          disabled={deleting !== t.id}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        className="btn-secondary text-xs text-red-600 hover:text-red-800 focus-ring"
                        onClick={() => setDeleting(t.id)}
                        aria-label={`Delete ${t.first_name || t.email}`}
                      >
                        🗑️ Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
      </>)}
      <CreateTeacherModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={async (newTeacher?: { email: string; password?: string }) => {
          // Refresh the teacher list
          let teachersWithClasses: TeacherUser[] = []
          try {
            const [teachersData, classesData] = await Promise.all([
              teachersApi.list(),
              classesApi.list(),
            ])
            const teachersList = Array.isArray(teachersData) ? (teachersData as TeacherUser[]) : []
            
            // Add class assignments to each teacher
            const classes = Array.isArray(classesData) ? classesData : []
            teachersWithClasses = teachersList.map(teacher => {
              const assignedClasses = classes
                .filter(cls => cls.teacher_names?.some(tName => {
                  const teacherName = `${teacher.first_name || ''} ${teacher.last_name || ''}`.trim()
                  return tName.includes(teacherName) || tName.includes(teacher.email)
                }))
                .map(cls => cls.name)
              
              return {
                ...teacher,
                class_assignments: assignedClasses,
              }
            })
            
            setTeachers(teachersWithClasses)
          } catch (error) {
            console.error('Failed to refresh teacher list:', error)
          }
          
          // If we have password from creation, preserve it temporarily for email sending
          if (newTeacher?.password && newTeacher?.email && teachersWithClasses.length > 0) {
            const teacherWithPassword = teachersWithClasses.find(t => t.email === newTeacher.email)
            if (teacherWithPassword) {
              teacherWithPassword.password = newTeacher.password
              // Automatically open email client
              setTimeout(() => {
                handleSendLoginEmail(teacherWithPassword)
              }, 500)
            }
          }
        }}
      />
      {editing && (
        <EditTeacherModal
          open={!!editing}
          onClose={() => setEditing(null)}
          teacher={editing}
          onSaved={(updated) => {
            // EditTeacherModal already persisted the change; just reflect it in the list.
            setTeachers((prev) => prev.map(p => p.id === updated.id ? (updated as TeacherUser) : p))
          }}
        />
      )}
    </motion.div>
  )
}


